import React, { useState, useEffect } from 'react';
import { Ticket } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';
import { Tag, DollarSign, Users } from 'lucide-react';

interface TicketFormProps {
  eventId: string;
  initialData?: Partial<Ticket>;
  onSubmit: (data: Partial<Ticket>) => Promise<void>;
  isSubmitting: boolean;
}

const TicketForm: React.FC<TicketFormProps> = ({
  eventId,
  initialData = {},
  onSubmit,
  isSubmitting
}) => {
  const [name, setName] = useState(initialData.name || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [price, setPrice] = useState(initialData.price?.toString() || '0');
  const [currency, setCurrency] = useState(initialData.currency || 'XAF');
  const [quantity, setQuantity] = useState(initialData.quantity?.toString() || '');
  const [availableQuantity, setAvailableQuantity] = useState(initialData.available_quantity?.toString() || '');
  const [benefits, setBenefits] = useState(initialData.benefits || '');
  const [ticketType, setTicketType] = useState(initialData.ticket_type || 'standard');
  
  // États de validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  const { showToast } = useToast();
  
  // Mettre à jour la quantité disponible lorsque la quantité totale change
  useEffect(() => {
    if (quantity && (!availableQuantity || parseInt(availableQuantity) > parseInt(quantity))) {
      setAvailableQuantity(quantity);
    }
  }, [quantity]);
  
  // Fonction de validation des champs
  const validateField = (name: string, value: string) => {
    let fieldErrors: Record<string, string> = {};
    
    switch (name) {
      case 'name':
        if (!value.trim()) {
          fieldErrors.name = 'Le nom du ticket est obligatoire';
        } else if (value.length < 2) {
          fieldErrors.name = 'Le nom doit contenir au moins 2 caractères';
        }
        break;
      case 'price':
        if (value === '' || isNaN(Number(value))) {
          fieldErrors.price = 'Le prix doit être un nombre';
        } else if (Number(value) < 0) {
          fieldErrors.price = 'Le prix ne peut pas être négatif';
        }
        break;
      case 'quantity':
        if (value && (isNaN(Number(value)) || Number(value) <= 0)) {
          fieldErrors.quantity = 'La quantité doit être un nombre positif';
        }
        break;
      case 'availableQuantity':
        if (value && isNaN(Number(value))) {
          fieldErrors.availableQuantity = 'La quantité disponible doit être un nombre';
        } else if (value && Number(value) < 0) {
          fieldErrors.availableQuantity = 'La quantité disponible ne peut pas être négative';
        } else if (value && quantity && Number(value) > Number(quantity)) {
          fieldErrors.availableQuantity = 'La quantité disponible ne peut pas dépasser la quantité totale';
        }
        break;
    }
    
    return fieldErrors;
  };
  
  // Gestion du changement des champs avec validation
  const handleFieldChange = (name: string, value: any) => {
    // Mettre à jour l'état du champ
    switch (name) {
      case 'name': setName(value); break;
      case 'description': setDescription(value); break;
      case 'price': setPrice(value); break;
      case 'currency': setCurrency(value); break;
      case 'quantity': setQuantity(value); break;
      case 'availableQuantity': setAvailableQuantity(value); break;
      case 'benefits': setBenefits(value); break;
      case 'ticketType': setTicketType(value); break;
    }
    
    // Marquer le champ comme touché
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const fieldErrors = validateField(name, value);
    setErrors(prev => {
      const newErrors = { ...prev };
      // Supprimer l'erreur précédente si elle existe
      if (name in newErrors) delete newErrors[name];
      // Ajouter la nouvelle erreur si elle existe
      if (name in fieldErrors) newErrors[name] = fieldErrors[name];
      return newErrors;
    });
  };
  
  // Validation du formulaire complet
  const validateForm = () => {
    const formErrors: Record<string, string> = {};
    
    // Valider chaque champ
    Object.assign(formErrors, validateField('name', name));
    Object.assign(formErrors, validateField('price', price));
    if (quantity) Object.assign(formErrors, validateField('quantity', quantity));
    if (availableQuantity) Object.assign(formErrors, validateField('availableQuantity', availableQuantity));
    
    setErrors(formErrors);
    setTouched({
      name: true,
      price: true,
      quantity: !!quantity,
      availableQuantity: !!availableQuantity,
      description: !!description,
      benefits: !!benefits
    });
    
    return Object.keys(formErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider tous les champs avant soumission
    if (!validateForm()) {
      showToast('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    
    const ticketData: Partial<Ticket> = {
      event_id: eventId,
      name,
      description: description || undefined,
      price: parseFloat(price),
      currency,
      quantity: quantity ? parseInt(quantity) : undefined,
      available_quantity: availableQuantity ? parseInt(availableQuantity) : undefined,
      benefits: benefits || undefined,
      ticket_type: ticketType as 'standard' | 'vip' | 'free' | 'early_bird',
      is_active: true
    };
    
    try {
      await onSubmit(ticketData);
    } catch (error) {
      // Error is handled in the parent component
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-1">
          Nom du ticket <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Tag className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
          </div>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => setTouched(prev => ({ ...prev, name: true }))}
            aria-invalid={errors.name ? 'true' : 'false'}
            aria-describedby={errors.name ? 'name-error' : undefined}
            required
            className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.name && touched.name ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100 placeholder-gray-400`}
            placeholder="Ex: Pass VIP, Entrée standard, etc."
          />
        </div>
        {errors.name && touched.name && (
          <p id="name-error" className="mt-1 text-sm text-red-400">{errors.name}</p>
        )}
      </div>
      
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-200 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100 placeholder-gray-400"
          placeholder="Description du ticket et ce qu'il inclut"
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-200 mb-1">
            Prix <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
            </div>
            <input
              type="number"
              id="price"
              name="price"
              value={price}
              onChange={(e) => handleFieldChange('price', e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, price: true }))}
              aria-invalid={errors.price ? 'true' : 'false'}
              aria-describedby={errors.price ? 'price-error' : undefined}
              min="0"
              step="0.01"
              required
              className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.price && touched.price ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
            />
          </div>
          {errors.price && touched.price ? (
            <p id="price-error" className="mt-1 text-sm text-red-400">{errors.price}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              Mettez 0 pour un ticket gratuit
            </p>
          )}
        </div>
        
        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-200 mb-1">
            Devise
          </label>
          <select
            id="currency"
            name="currency"
            value={currency}
            onChange={(e) => handleFieldChange('currency', e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100"
          >
            <option value="XAF" className="bg-gray-800">XAF (FCFA)</option>
            <option value="USD" className="bg-gray-800">USD (Dollar américain)</option>
            <option value="EUR" className="bg-gray-800">EUR (Euro)</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-200 mb-1">
            Quantité totale
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Users className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
            </div>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={quantity}
              onChange={(e) => handleFieldChange('quantity', e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, quantity: true }))}
              aria-invalid={errors.quantity ? 'true' : 'false'}
              aria-describedby={errors.quantity ? 'quantity-error' : undefined}
              min="1"
              className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.quantity && touched.quantity ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
            />
          </div>
          {errors.quantity && touched.quantity ? (
            <p id="quantity-error" className="mt-1 text-sm text-red-400">{errors.quantity}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              Laissez vide pour une quantité illimitée
            </p>
          )}
        </div>
        
        <div>
          <label htmlFor="availableQuantity" className="block text-sm font-medium text-gray-200 mb-1">
            Quantité disponible
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Users className="h-5 w-5 text-yellow-400/70" aria-hidden="true" />
            </div>
            <input
              type="number"
              id="availableQuantity"
              name="availableQuantity"
              value={availableQuantity}
              onChange={(e) => handleFieldChange('availableQuantity', e.target.value)}
              onBlur={() => setTouched(prev => ({ ...prev, availableQuantity: true }))}
              aria-invalid={errors.availableQuantity ? 'true' : 'false'}
              aria-describedby={errors.availableQuantity ? 'availableQuantity-error' : undefined}
              min="0"
              max={quantity || undefined}
              className={`w-full pl-10 px-3 py-2 bg-white/5 border ${errors.availableQuantity && touched.availableQuantity ? 'border-red-500' : 'border-white/10'} rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100`}
            />
          </div>
          {errors.availableQuantity && touched.availableQuantity ? (
            <p id="availableQuantity-error" className="mt-1 text-sm text-red-400">{errors.availableQuantity}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              Nombre de tickets disponibles à la vente
            </p>
          )}
        </div>
      </div>
      
      <div>
        <label htmlFor="benefits" className="block text-sm font-medium text-gray-200 mb-1">
          Avantages
        </label>
        <textarea
          id="benefits"
          name="benefits"
          value={benefits}
          onChange={(e) => handleFieldChange('benefits', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100 placeholder-gray-400"
          placeholder="Ex: Accès VIP, boisson offerte, etc."
        />
      </div>
      
      <div>
        <label htmlFor="ticketType" className="block text-sm font-medium text-gray-200 mb-1">
          Type de ticket
        </label>
        <select
          id="ticketType"
          name="ticketType"
          value={ticketType}
          onChange={(e) => handleFieldChange('ticketType', e.target.value)}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400/50 text-gray-100"
        >
          <option value="standard" className="bg-gray-800">Standard</option>
          <option value="vip" className="bg-gray-800">VIP</option>
          <option value="free" className="bg-gray-800">Gratuit</option>
          <option value="early_bird" className="bg-gray-800">Early Bird</option>
        </select>
      </div>
      
      <div className="flex justify-end pt-4 border-t border-white/10">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
              Enregistrement...
            </>
          ) : (
            'Enregistrer'
          )}
        </button>
      </div>
    </form>
  );
};

export default TicketForm;
